import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Plus, Search, Loader2, Edit2, ToggleLeft, ToggleRight, Users, RotateCcw } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Badge } from "shared/ui/badge"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "shared/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Switch } from "shared/ui/switch"
import {
  RoleDto,
  searchRoles,
  searchUsersForRole,
  type RoleUserLookupDto,
} from "modules/admin/api/roleService"
import {
  createUserRoleAssignment,
  searchUserRoleAssignments,
  updateUserRoleAssignment,
  type UserRoleAssignmentDto,
} from "modules/admin/api/userRoleAssignmentService"
import { fetchAreaOptions, type AreaDto } from "modules/admin/api/areaService"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

interface RoleUserMappingProps {
  currentUser?: {
    name?: string
    tenantId?: string
    tenantIds?: string[]
    tenants?: Array<{
      tenantId?: string | null
    }>
  }
}

type AssignmentFormState = {
  userId: string
  roleId: string
  departmentId: string
  isActive: boolean
}

const initialForm: AssignmentFormState = {
  userId: "",
  roleId: "",
  departmentId: "",
  isActive: true,
}

const NO_AREA_VALUE = "__none__"

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toISOString().slice(0, 10)
}

const formatUserLabel = (item?: UserRoleAssignmentDto | RoleUserLookupDto | null) => {
  if (!item) return "-"
  return item.fullName ?? item.username ?? item.email ?? item.userId ?? "-"
}

const normalizeSearchToken = (value?: string | null) =>
  (value ?? "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "")
    .trim()

const normalizeTenantId = (value?: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export function RoleUserMapping({ currentUser }: RoleUserMappingProps) {
  const tenantId = DEFAULT_TENANT_ID
  const searchableTenantIds = useMemo(() => {
    const tenantSet = new Set<string>()

    currentUser?.tenantIds?.forEach((value) => {
      const normalized = normalizeTenantId(value)
      if (normalized) {
        tenantSet.add(normalized)
      }
    })

    currentUser?.tenants?.forEach((tenant) => {
      const normalized = normalizeTenantId(tenant?.tenantId)
      if (normalized) {
        tenantSet.add(normalized)
      }
    })

    const fallbackTenantId = normalizeTenantId(currentUser?.tenantId) ?? tenantId
    if (tenantSet.size === 0 && fallbackTenantId) {
      tenantSet.add(fallbackTenantId)
    }

    return Array.from(tenantSet)
  }, [currentUser?.tenantId, currentUser?.tenantIds, currentUser?.tenants, tenantId])

  const [allItems, setAllItems] = useState<UserRoleAssignmentDto[]>([])

  const createDefaultFilters = () => ({ searchTerm: "", roleIds: [] as string[], areaIds: [] as string[] })
  const [stagedFilters, setStagedFilters] = useState(createDefaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(createDefaultFilters)

  const [roleSearchTerm, setRoleSearchTerm] = useState("")
  const [areaSearchTerm, setAreaSearchTerm] = useState("")

  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [isLoading, setIsLoading] = useState(false)

  const [roles, setRoles] = useState<RoleDto[]>([])
  const [areas, setAreas] = useState<AreaDto[]>([])
  const [users, setUsers] = useState<RoleUserLookupDto[]>([])
  const [isUserLoading, setIsUserLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<UserRoleAssignmentDto | null>(null)
  const [form, setForm] = useState<AssignmentFormState>(initialForm)
  const [userSearch, setUserSearch] = useState("")
  const [roleSearch, setRoleSearch] = useState("")
  const [areaSearch, setAreaSearch] = useState("")

  const isEditing = Boolean(editing)

  const toggleValue = (list: string[], value: string, checked: boolean) => {
    if (checked) return list.includes(value) ? list : [...list, value]
    return list.filter((item) => item !== value)
  }

  const totalItems = allItems.length
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize])
  const items = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    return allItems.slice(startIndex, startIndex + pageSize)
  }, [allItems, page, pageSize])

  const selectedUser = useMemo(
    () => users.find((u) => u.userId === form.userId) ?? null,
    [form.userId, users],
  )

  const filteredRoles = useMemo(() => {
    if (!roleSearch.trim()) return roles
    const term = roleSearch.trim().toLowerCase()
    return roles.filter(
      (role) =>
        role.code.toLowerCase().includes(term) ||
        role.displayName.toLowerCase().includes(term),
    )
  }, [roleSearch, roles])

  const filteredAreas = useMemo(() => {
    if (!areaSearch.trim()) return areas
    const rawTerm = areaSearch.trim().toLowerCase()
    const normalizedTerm = normalizeSearchToken(areaSearch)
    return areas.filter(
      (area) => {
        const code = area.code ?? ""
        const nameTh = area.nameTh ?? ""
        const nameEn = area.nameEn ?? ""

        const codeRawMatch = code.toLowerCase().includes(rawTerm)
        const nameThRawMatch = nameTh.toLowerCase().includes(rawTerm)
        const nameEnRawMatch = nameEn.toLowerCase().includes(rawTerm)

        if (codeRawMatch || nameThRawMatch || nameEnRawMatch) {
          return true
        }

        if (!normalizedTerm) {
          return false
        }

        return (
          normalizeSearchToken(code).includes(normalizedTerm) ||
          normalizeSearchToken(nameTh).includes(normalizedTerm) ||
          normalizeSearchToken(nameEn).includes(normalizedTerm)
        )
      },
    )
  }, [areaSearch, areas])

  const filterBarRoles = useMemo(() => {
    const term = roleSearchTerm.trim().toLowerCase()
    if (!term) return roles
    return roles.filter(
      (r) => r.code.toLowerCase().includes(term) || r.displayName.toLowerCase().includes(term),
    )
  }, [roleSearchTerm, roles])

  const filterBarAreas = useMemo(() => {
    const rawTerm = areaSearchTerm.trim().toLowerCase()
    if (!rawTerm) return areas
    const normalizedTerm = normalizeSearchToken(areaSearchTerm)
    return areas.filter((area) => {
      const code = area.code ?? ""
      const nameTh = area.nameTh ?? ""
      if (code.toLowerCase().includes(rawTerm) || nameTh.toLowerCase().includes(rawTerm)) return true
      if (!normalizedTerm) return false
      return normalizeSearchToken(code).includes(normalizedTerm) || normalizeSearchToken(nameTh).includes(normalizedTerm)
    })
  }, [areaSearchTerm, areas])

  const loadAssignments = useCallback(
    async () => {
      setIsLoading(true)
      try {
        // Assignment list intentionally searches across accessible tenants instead of filtering to a single tenant.
        console.log("DEBUG loadAssignments request:", {
          searchableTenantIds,
          roleIds: appliedFilters.roleIds,
          areaIds: appliedFilters.areaIds,
          searchTerm: appliedFilters.searchTerm,
        })
        const results = await Promise.allSettled(
          searchableTenantIds.map((searchTenantId) =>
            searchUserRoleAssignments({
              tenantId: searchTenantId,
              search: appliedFilters.searchTerm || undefined,
              roleIds: appliedFilters.roleIds.length > 0 ? appliedFilters.roleIds : undefined,
              departmentIds: appliedFilters.areaIds.length > 0 ? appliedFilters.areaIds : undefined,
              page: 1,
              pageSize: 200,
            }),
          ),
        )

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            console.log("DEBUG loadAssignments response:", {
              tenantId: searchableTenantIds[index],
              totalItems: result.value.totalItems,
              itemsReturned: result.value.items?.length,
            })
          } else {
            console.log("DEBUG loadAssignments FAILED for tenant:", searchableTenantIds[index], result.reason)
          }
        })

        const mergedAssignments = new Map<string, UserRoleAssignmentDto>()
        results.forEach((result) => {
          if (result.status !== "fulfilled") {
            return
          }

          result.value.items?.forEach((item) => {
            if (!item?.userRoleId || mergedAssignments.has(item.userRoleId)) {
              return
            }
            mergedAssignments.set(item.userRoleId, item)
          })
        })

        const sortedAssignments = Array.from(mergedAssignments.values()).sort((left, right) => {
          const leftLabel = `${formatUserLabel(left)} ${left.roleName ?? left.roleCode ?? ""} ${left.departmentName ?? left.departmentCode ?? ""}`
          const rightLabel = `${formatUserLabel(right)} ${right.roleName ?? right.roleCode ?? ""} ${right.departmentName ?? right.departmentCode ?? ""}`
          return leftLabel.localeCompare(rightLabel, "th-TH")
        })

        setAllItems(sortedAssignments)
        setPage(1)
      } catch (error) {
        console.error(error)
        toast.error("โหลดข้อมูล User-Role Mapping ไม่สำเร็จ")
      } finally {
        setIsLoading(false)
      }
    },
    [searchableTenantIds, appliedFilters.searchTerm, appliedFilters.roleIds, appliedFilters.areaIds],
  )

  const loadRoles = useCallback(async () => {
    try {
      const result = await searchRoles({ page: 1, pageSize: 200 })
      setRoles(result.items ?? [])
    } catch (error) {
      console.error(error)
      toast.error("โหลดรายการ Role ไม่สำเร็จ")
    }
  }, [])

  const loadAreas = useCallback(async () => {
    try {
      // Area lookup intentionally searches across accessible tenants instead of filtering to a single tenant.
      const results = await Promise.allSettled(
        searchableTenantIds.map((searchTenantId) => fetchAreaOptions(searchTenantId, "all")),
      )

      const mergedAreas = new Map<string, AreaDto>()
      results.forEach((result) => {
        if (result.status !== "fulfilled") {
          return
        }

        result.value.forEach((item) => {
          if (!item?.areaId || mergedAreas.has(item.areaId)) {
            return
          }
          mergedAreas.set(item.areaId, item)
        })
      })

      setAreas(
        Array.from(mergedAreas.values()).sort((left, right) =>
          `${left.nameTh ?? ""} ${left.code ?? ""}`.localeCompare(`${right.nameTh ?? ""} ${right.code ?? ""}`, "th-TH"),
        ),
      )
    } catch (error) {
      console.error(error)
      toast.error("โหลดรายการ Area ไม่สำเร็จ")
    }
  }, [searchableTenantIds])

  const loadUsers = useCallback(async () => {
    setIsUserLoading(true)
    try {
      // User lookup intentionally searches across accessible tenants instead of filtering to a single tenant.
      const results = await Promise.allSettled(
        searchableTenantIds.map((searchTenantId) =>
          searchUsersForRole({
            tenantId: searchTenantId,
            search: userSearch || undefined,
            page: 1,
            pageSize: 20,
          }),
        ),
      )

      const mergedUsers = new Map<string, RoleUserLookupDto>()
      results.forEach((result, index) => {
        if (result.status !== "fulfilled") {
          return
        }

        const resultTenantId = searchableTenantIds[index]

        result.value.items?.forEach((item) => {
          if (!item.userId || mergedUsers.has(item.userId)) {
            return
          }
          mergedUsers.set(item.userId, {
            ...item,
            tenantId: resultTenantId,
          })
        })
      })

      setUsers(
        Array.from(mergedUsers.values()).sort((left, right) =>
          formatUserLabel(left).localeCompare(formatUserLabel(right), "th-TH"),
        ),
      )
    } catch (error) {
      console.error(error)
      toast.error("โหลดรายการผู้ใช้ไม่สำเร็จ")
    } finally {
      setIsUserLoading(false)
    }
  }, [searchableTenantIds, userSearch])

  useEffect(() => {
    loadAssignments()
  }, [loadAssignments])

  useEffect(() => {
    setPage(1)
  }, [appliedFilters])

  useEffect(() => {
    loadRoles()
    loadAreas()
  }, [loadRoles, loadAreas])

  useEffect(() => {
    if (!dialogOpen || isEditing) return
    const handler = setTimeout(() => {
      loadUsers()
    }, 250)
    return () => clearTimeout(handler)
  }, [dialogOpen, isEditing, loadUsers, userSearch])

  const openCreate = () => {
    setEditing(null)
    setForm(initialForm)
    setUsers([])
    setUserSearch("")
    setRoleSearch("")
    setAreaSearch("")
    setDialogOpen(true)
  }

  const openEdit = (item: UserRoleAssignmentDto) => {
    setEditing(item)
    setForm({
      userId: item.userId,
      roleId: item.roleId,
      departmentId: item.departmentId ?? NO_AREA_VALUE,
      isActive: item.isActive,
    })
    setUsers([
      {
        userId: item.userId,
        tenantId: item.tenantId,
        fullName: item.fullName ?? undefined,
        email: item.email ?? undefined,
        username: item.username ?? undefined,
        phone: null,
      },
    ])
    setUserSearch("")
    setRoleSearch("")
    setAreaSearch("")
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.userId) {
      toast.error("กรุณาเลือกผู้ใช้")
      return
    }
    if (!form.roleId) {
      toast.error("กรุณาเลือก Role")
      return
    }
    const resolvedDepartmentId =
      form.departmentId && form.departmentId !== NO_AREA_VALUE ? form.departmentId : null
    const selectedUserTenantId = normalizeTenantId(selectedUser?.tenantId)
    const editingTenantId = normalizeTenantId(editing?.tenantId)
    const resolvedTenantId = selectedUserTenantId ?? editingTenantId ?? tenantId

    try {
      if (isEditing && editing) {
        await updateUserRoleAssignment(editing.userRoleId, {
          tenantId: resolvedTenantId,
          roleId: form.roleId,
          departmentId: resolvedDepartmentId,
          isActive: form.isActive,
        })
        toast.success("อัปเดต User-Role Mapping สำเร็จ")
      } else {
        await createUserRoleAssignment({
          tenantId: resolvedTenantId,
          userId: form.userId,
          roleId: form.roleId,
          departmentId: resolvedDepartmentId,
        })
        toast.success("บันทึก User-Role Mapping สำเร็จ")
      }
      setDialogOpen(false)
      loadAssignments()
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "บันทึก User-Role Mapping ไม่สำเร็จ"
      toast.error(typeof message === "string" ? message : "บันทึก User-Role Mapping ไม่สำเร็จ")
    }
  }

  const handleToggleActive = async (item: UserRoleAssignmentDto) => {
    try {
      await updateUserRoleAssignment(item.userRoleId, {
        tenantId: item.tenantId,
        roleId: item.roleId,
        departmentId: item.departmentId ?? null,
        isActive: !item.isActive,
      })
      toast.success(`เปลี่ยนสถานะเป็น ${!item.isActive ? "Active" : "Inactive"}`)
      loadAssignments()
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data ?? "อัปเดตสถานะไม่สำเร็จ"
      toast.error(typeof message === "string" ? message : "อัปเดตสถานะไม่สำเร็จ")
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Users className="h-5 w-5 text-blue-600" />
              User-Role Mapping
            </CardTitle>
            <p className="text-sm text-gray-500">
              จัดการความสัมพันธ์ User / Role / Area{currentUser?.name ? ` (admin: ${currentUser.name})` : ""}
            </p>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" /> เพิ่มรายการใหม่
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full md:w-80">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              <Input
                placeholder="ค้นหาผู้ใช้ / Role / Area"
                value={stagedFilters.searchTerm}
                onChange={(event) => setStagedFilters((prev) => ({ ...prev, searchTerm: event.target.value }))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    setAppliedFilters(stagedFilters)
                  }
                }}
                className="pl-9"
              />
            </div>

            {/* Role multi-select */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-44 justify-between">
                  <span className="truncate">
                    {stagedFilters.roleIds.length === 0
                      ? "Role ทั้งหมด"
                      : stagedFilters.roleIds.length === 1
                        ? (roles.find((r) => r.roleId === stagedFilters.roleIds[0])?.displayName ?? "1 role")
                        : `${stagedFilters.roleIds.length} roles`}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-80 overflow-y-auto">
                <div className="px-2 py-2">
                  <Input
                    placeholder="ค้นหา role..."
                    value={roleSearchTerm}
                    onChange={(e) => setRoleSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="h-8"
                  />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={stagedFilters.roleIds.length === 0}
                  onCheckedChange={() => setStagedFilters((prev) => ({ ...prev, roleIds: [] }))}
                  onSelect={(e) => e.preventDefault()}
                >
                  ทั้งหมด
                </DropdownMenuCheckboxItem>
                {filterBarRoles.map((role) => (
                  <DropdownMenuCheckboxItem
                    key={role.roleId}
                    checked={stagedFilters.roleIds.includes(role.roleId)}
                    onCheckedChange={(checked) =>
                      setStagedFilters((prev) => ({
                        ...prev,
                        roleIds: toggleValue(prev.roleIds, role.roleId, checked === true),
                      }))
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    {role.displayName} ({role.code})
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Area multi-select */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-52 justify-between">
                  <span className="truncate">
                    {stagedFilters.areaIds.length === 0
                      ? "Area ทั้งหมด"
                      : stagedFilters.areaIds.length === 1
                        ? (areas.find((a) => a.areaId === stagedFilters.areaIds[0])?.nameTh ?? "1 area")
                        : `${stagedFilters.areaIds.length} areas`}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto">
                <div className="px-2 py-2">
                  <Input
                    placeholder="ค้นหา area..."
                    value={areaSearchTerm}
                    onChange={(e) => setAreaSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="h-8"
                  />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={stagedFilters.areaIds.length === 0}
                  onCheckedChange={() => setStagedFilters((prev) => ({ ...prev, areaIds: [] }))}
                  onSelect={(e) => e.preventDefault()}
                >
                  ทั้งหมด
                </DropdownMenuCheckboxItem>
                {filterBarAreas.map((area) => (
                  <DropdownMenuCheckboxItem
                    key={area.areaId}
                    checked={stagedFilters.areaIds.includes(area.areaId)}
                    onCheckedChange={(checked) =>
                      setStagedFilters((prev) => ({
                        ...prev,
                        areaIds: toggleValue(prev.areaIds, area.areaId, checked === true),
                      }))
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    {area.nameTh} ({area.code})
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={() => {
                setAppliedFilters(stagedFilters)
              }}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "ค้นหา"}
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                const reset = createDefaultFilters()
                setStagedFilters(reset)
                setAppliedFilters(reset)
                setRoleSearchTerm("")
                setAreaSearchTerm("")
              }}
            >
              <RotateCcw className="h-4 w-4 mr-1" /> ล้างตัวกรอง
            </Button>

            <div className="text-sm text-gray-500">
              ทั้งหมด {totalItems.toLocaleString("th-TH")} รายการ
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ผู้ใช้</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Area</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>เริ่มต้น</TableHead>
                <TableHead className="text-right">การจัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-gray-500">
                    กำลังโหลดข้อมูล...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-gray-500">
                    ไม่พบรายการ
                  </TableCell>
                </TableRow>
              ) : (
                  items.map((item) => (
                    <TableRow key={item.userRoleId}>
                      <TableCell>
                        <div className="font-medium">{formatUserLabel(item)}</div>
                        <div className="text-xs text-gray-500">{item.email ?? item.userId}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{item.roleName ?? item.roleCode ?? item.roleId}</div>
                        <div className="text-xs text-gray-500">{item.roleCode}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{item.departmentName ?? "-"}</div>
                        <div className="text-xs text-gray-500">{item.departmentCode}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.isActive ? "default" : "secondary"}>
                          {item.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(item.validFrom)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="gap-1">
                            <Edit2 className="h-4 w-4" /> แก้ไข
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(item)}
                            className="gap-1"
                          >
                            {item.isActive ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-gray-500" />
                            )}
                            {item.isActive ? "ปิด" : "เปิด"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>หน้า {page} / {totalPages}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ก่อนหน้า
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                ถัดไป
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? "แก้ไข User-Role Mapping" : "เพิ่ม User-Role Mapping"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>ผู้ใช้</Label>
              <Select
                value={form.userId}
                onValueChange={(value) => setForm((prev) => ({ ...prev, userId: value }))}
                onOpenChange={(open) => {
                  if (open) {
                    loadUsers()
                  }
                }}
                disabled={isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือกผู้ใช้" />
                </SelectTrigger>
                <SelectContent className="max-h-72 p-0">
                  <div className="sticky top-0 z-10 border-b bg-white p-2">
                    <Input
                      placeholder="ค้นหา ชื่อ / username / email"
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                    />
                  </div>
                  {isUserLoading ? (
                    <div className="px-3 py-3 text-center text-sm text-gray-500">กำลังโหลด...</div>
                  ) : users.length === 0 ? (
                    <div className="px-3 py-3 text-center text-sm text-gray-500">ยังไม่มีผลการค้นหา</div>
                  ) : (
                    users.map((user) => (
                      <SelectItem key={user.userId} value={user.userId}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">{formatUserLabel(user)}</span>
                          <span className="text-xs text-gray-500">{user.email ?? user.username}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <div className="text-xs text-gray-500">
                ผู้ใช้ที่เลือก: {formatUserLabel(selectedUser)}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={form.roleId}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, roleId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือก Role" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 p-0">
                    <div className="sticky top-0 z-10 border-b bg-white p-2">
                      <Input
                        placeholder="ค้นหา role"
                        value={roleSearch}
                        onChange={(event) => setRoleSearch(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </div>
                    {filteredRoles.length === 0 ? (
                      <div className="px-3 py-3 text-center text-sm text-gray-500">ไม่พบ Role</div>
                    ) : (
                      filteredRoles.map((role) => (
                        <SelectItem key={role.roleId} value={role.roleId}>
                          {role.displayName} ({role.code})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Area</Label>
                <Select
                  value={form.departmentId}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, departmentId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือก Area (ถ้ามี)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 p-0">
                    <div className="sticky top-0 z-10 border-b bg-white p-2">
                      <Input
                        placeholder="ค้นหา area"
                        value={areaSearch}
                        onChange={(event) => setAreaSearch(event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      />
                    </div>
                    <SelectItem value={NO_AREA_VALUE}>ไม่ระบุ</SelectItem>
                    {filteredAreas.map((area) => (
                      <SelectItem key={area.areaId} value={area.areaId}>
                        {area.nameTh} ({area.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="user-role-active"
                checked={form.isActive}
                onCheckedChange={(value) => setForm((prev) => ({ ...prev, isActive: value }))}
              />
              <Label htmlFor="user-role-active">ใช้งาน (Active)</Label>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              ยกเลิก
            </Button>
            <Button onClick={handleSubmit}>{isEditing ? "อัปเดต" : "บันทึก"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RoleUserMapping
